import '../../css/settings/animations.scss'
import {type ImageURL, LobbyImage} from '@colonist/types'
import {type TranslationKeys, type TranslationObject, type TranslationOptions} from '@colonist/i18n'
import {EnumUtils} from '@colonist/utils'
import {clientI18n} from '../ClientI18n.ts'
import {getLobbyImagePath} from './Utils.ts'
import {UIDeviceController} from '../ui_html/UIDeviceController.ts'

export let translate: (key: TranslationKeys, options?: TranslationOptions) => string
export function initTranslation(translationFunction: (key: TranslationKeys, options?: TranslationOptions) => string) {
    translate = translationFunction
}

function assignBaseElementProps(element: Element, container: Element, className: string, id: string) {
    element.className = className
    element.id = id

    container.appendChild(element)
}

export function createDiv(className?: string, id?: string): HTMLDivElement {
    const div: HTMLDivElement = document.createElement('DIV') as HTMLDivElement
    if(className !== undefined) div.className = className
    if(id !== undefined) div.id = id
    return div
}

export function createScriptIn(container: HTMLElement, src: string): HTMLScriptElement {
    const script: HTMLScriptElement = document.createElement('script') as HTMLScriptElement
    script.src = src

    assignBaseElementProps(script, container, '', '')
    return script
}

export function createTableIn(container: HTMLElement, className: string = ''): HTMLTableElement {
    const table: HTMLTableElement = document.createElement('TABLE') as HTMLTableElement

    assignBaseElementProps(table, container, className, '')
    return table
}

export function createTHEADIn(container: Element, className: string = '', id: string = ''): HTMLTableSectionElement {
    const thead: HTMLTableSectionElement = document.createElement('THEAD') as HTMLTableSectionElement
    assignBaseElementProps(thead, container, className, id)
    container.appendChild(thead)
    return thead
}

export function createTBIn(container: Element, className: string = '', id: string = ''): HTMLTableSectionElement {
    const tbody: HTMLTableSectionElement = document.createElement('TBODY') as HTMLTableSectionElement
    assignBaseElementProps(tbody, container, className, id)
    container.appendChild(tbody)
    return tbody
}

export function createTRIn(container: HTMLElement, id: string, className: string = ''): HTMLTableRowElement {
    const tr: HTMLTableRowElement = document.createElement('TR') as HTMLTableRowElement

    assignBaseElementProps(tr, container, className, id)
    return tr
}

export function createTDIn(container: Element, className: string = '', text?: TranslationObject): HTMLTableCellElement {
    const td: HTMLTableCellElement = document.createElement('TD') as HTMLTableCellElement
    if(text !== undefined) setElementInnerHTML(td, text)

    assignBaseElementProps(td, container, className, '')
    return td
}

export function createTHIn(container: Element, className: string = '', text?: TranslationObject): HTMLTableSectionElement {
    const th: HTMLTableSectionElement = document.createElement('TH') as HTMLTableSectionElement
    if(text !== undefined) setElementInnerHTML(th, text)

    assignBaseElementProps(th, container, className, '')
    return th
}

export function createDivIn(container: HTMLElement, className: string, id: string = ''): HTMLDivElement {
    const div: HTMLDivElement = document.createElement('DIV') as HTMLDivElement

    assignBaseElementProps(div, container, className, id)
    return div
}

export function createSpanIn(container: HTMLElement, className: string, text: TranslationObject): HTMLSpanElement {
    const span: HTMLSpanElement = document.createElement('SPAN') as HTMLSpanElement
    setElementInnerHTML(span, text)

    assignBaseElementProps(span, container, className, '')
    return span
}

export function createFormIn(container: HTMLElement, className: string, id: string = ''): HTMLFormElement {
    const form: HTMLFormElement = document.createElement('FORM') as HTMLFormElement

    assignBaseElementProps(form, container, className, id)
    return form
}

export function createInputIn(container: HTMLElement, className: string, id: string = ''): HTMLInputElement {
    const input: HTMLInputElement = document.createElement('INPUT') as HTMLInputElement

    assignBaseElementProps(input, container, className, id)
    return input
}

export function createTextareaIn(container: HTMLElement, className: string, id: string = ''): HTMLTextAreaElement {
    const textarea: HTMLTextAreaElement = document.createElement('TEXTAREA') as HTMLTextAreaElement

    assignBaseElementProps(textarea, container, className, id)
    return textarea
}

export function createSliderIn(container: HTMLElement, className: string, id: string = '', min: number, max: number, step: number, initialValue: number): HTMLInputElement {
    const input: HTMLInputElement = document.createElement('INPUT') as HTMLInputElement
    input.type = 'range'
    input.min = min.toString()
    input.max = max.toString()
    input.step = step.toString()
    input.value = initialValue.toString()

    assignBaseElementProps(input, container, className, id)
    return input
}

export function createCheckboxIn(container: HTMLElement, className: string, id: string = '',
                                 onclick: (event: Event) => void): HTMLInputElement {
    const input = createInputIn(container, className, id)
    input.type = 'checkbox'
    // eslint-disable-next-line no-restricted-syntax
    input.onclick = onclick
    return input
}

export function createHeaderIn(container: HTMLElement, text: TranslationObject, className: string, id: string = '',
                               hType: string = 'H3'): HTMLHeadingElement {
    const header: HTMLHeadingElement = document.createElement(hType) as HTMLHeadingElement
    setElementInnerHTML(header, text)

    assignBaseElementProps(header, container, className, id)
    return header
}

export function createParagraphIn(container: HTMLElement, text: TranslationObject, className: string, id: string = ''): HTMLParagraphElement {
    const element: HTMLParagraphElement = document.createElement('P') as HTMLParagraphElement
    setElementInnerHTML(element, text)

    assignBaseElementProps(element, container, className, id)
    return element
}

export function createLinkIn(container: HTMLElement, href: string, text: TranslationObject, className: string, id: string = '', openNewTab: boolean = false): HTMLLinkElement {
    const element: HTMLLinkElement = document.createElement('A') as HTMLLinkElement
    setElementInnerHTML(element, text)
    element.href = href
    if(openNewTab) element.target = '_blank'

    assignBaseElementProps(element, container, className, id)
    return element
}

export function createImgIn(container: HTMLElement, src: LobbyImage, className: string = '', id: string = '') {
    const element: HTMLImageElement = document.createElement('IMG') as HTMLImageElement
    setImageElementAlt(element, src)
    element.src = getLobbyImagePath(src)

    assignBaseElementProps(element, container, className, id)
    return element
}

export function createExternalImgIn(container: HTMLElement, src: ImageURL, alt: TranslationObject, className: string = '') {
    const element: HTMLImageElement = document.createElement('IMG') as HTMLImageElement
    element.alt = translate(alt.key, alt.options)
    element.src = src

    assignBaseElementProps(element, container, className, '')
    return element
}

export function createOptionIn(container: HTMLSelectElement, value: string, text: TranslationObject, defaultSelected: boolean = false) {
    const element: HTMLOptionElement = document.createElement('OPTION') as HTMLOptionElement
    container.appendChild(element)
    element.value = value
    element.innerText = translate(text.key, text.options)
    element.selected = defaultSelected
    return element
}

export function createSelectIn(container: HTMLElement, className: string, id: string = '',
                               onchange: (value: string) => void): HTMLSelectElement {
    const element: HTMLSelectElement = document.createElement('SELECT') as HTMLSelectElement
    element.onchange = () => {
        onchange(element.value)
    }

    assignBaseElementProps(element, container, className, id)
    return element
}

export function updateSelectElement(element: HTMLSelectElement, setting: string) {
    if(element.options[element.selectedIndex].value == setting) return
    for(const option of Array.from(element.options)) {
        if(option.value == setting) {
            option.selected = true
            break
        }
    }
}

export function createButtonIn(container: HTMLElement, className: string, text: TranslationObject): HTMLButtonElement {
    const button: HTMLButtonElement = document.createElement('BUTTON') as HTMLButtonElement
    setElementInnerHTML(button, text)

    assignBaseElementProps(button, container, className, '')
    return button
}

export function createIFrameIn(container: HTMLElement, width: string, height: string, src: string, className: string = ''): HTMLIFrameElement {
    const iframe: HTMLIFrameElement = document.createElement('IFRAME') as HTMLIFrameElement
    iframe.width = width
    iframe.height = height
    iframe.src = src
    iframe.frameBorder = '0'

    assignBaseElementProps(iframe, container, className, '')
    return iframe
}

export function createListItemIn(container: HTMLUListElement | HTMLOListElement, text: TranslationObject, className: string, id: string = ''): HTMLLIElement {
    const element: HTMLLIElement = document.createElement('LI') as HTMLLIElement
    setElementInnerHTML(element, text)

    // must be last call to handle fragments
    assignBaseElementProps(element, container, className, id)
    return element
}

export function createUnorderedListIn(container: HTMLElement, className: string, id: string = ''): HTMLUListElement {
    const element: HTMLUListElement = document.createElement('UL') as HTMLUListElement

    // must be last call to handle fragments to handle fragments
    assignBaseElementProps(element, container, className, id)
    return element
}

export function setImageElementAlt(element: HTMLImageElement, src: LobbyImage) {
    const altKey = EnumUtils.getEnumKey(src, LobbyImage)
    const altText: TranslationObject = {key: `strings:imgAlts.${altKey}`}
    element.alt = translate(altText.key, altText.options)
}

export function setElementInnerHTML(element: HTMLElement, text: TranslationObject) {
    const isTextAlreadyTranslated = text.key === 'strings:colonistCommon.utils.#buffer'
    const innerHTML = isTextAlreadyTranslated ? text.options?.value : translate(text.key, text.options)
    // eslint-disable-next-line no-restricted-syntax
    element.innerHTML = innerHTML
}

export function setElementInnerText(element: HTMLElement, text: TranslationObject) {
    const isTextAlreadyTranslated = text.key === 'strings:colonistCommon.utils.#buffer'
    const innerText = isTextAlreadyTranslated ? text.options?.value : clientI18n.t(text.key, text.options)

    element.innerText = innerText
}

/**
 * Use this function for nearly all use cases, so that mobile uses touch events
 *
 * Examples of when to use `onclick` or `addEventListener` instead:
 *
 * - To use the event target (or other event properties)
 * - When you want precise control over event handling
 * - When a unique HTML element, like an input, requires a click event to function properly
 * - When attaching a listener to the window or document object
 * - When you want to add multiple listeners to the same element
 */
export function setElementOnClick(element: HTMLElement, onClick: (event: Event) => void | Promise<void>, preventDefault: boolean = false, stopPropagation: boolean = false) {
    const hasTouchSupport = 'ontouchstart' in window || navigator.maxTouchPoints > 0
    const shouldUseTouchEvents = UIDeviceController.isUserOnMobileWebview() && !UIDeviceController.isCypressTest() && hasTouchSupport

    if(shouldUseTouchEvents) {
        const MAX_ERROR = 5
        let startX: number
        let startY: number

        element.ontouchstart = (event: TouchEvent) => {
            const touch = event.touches[0]
            startX = touch.clientX
            startY = touch.clientY
        }

        element.ontouchend = async(event: TouchEvent) => {
            const touch = event.changedTouches[0]
            const xDelta = Math.abs(touch.clientX - startX)
            const yDelta = Math.abs(touch.clientY - startY)

            if(xDelta > MAX_ERROR) return
            if(yDelta > MAX_ERROR) return

            event.preventDefault()
            if(stopPropagation) event.stopPropagation()
            await onClick(event)
        }

        element.style.transition = 'all 0s'
        return
    }

    // eslint-disable-next-line no-restricted-syntax
    element.onclick = async(event) => {
        if(preventDefault) event.preventDefault()
        if(stopPropagation) event.stopPropagation()
        await onClick(event)
    }
}
